import * as React from 'react';
import { Loader2, Lock, Play, Sparkles, Sparkle, Wand2 } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import {
  ADMIN_LOCKED_PROMPT_FIELDS,
  LLM_CATALOG,
  findModel,
  type LlmProvider,
} from '@cp3/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input, Textarea } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import { Slider } from '../../../../components/ui/slider';
import { Switch } from '../../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { useToast } from '../../../../components/ui/toast';
import { useTargetUidPayload } from '../../../../hooks/useTargetUidPayload';
import { functions } from '../../../../lib/firebase';
import { OnboardingWizard } from '../../../../components/onboarding/OnboardingWizard';
import type { FormState } from '../state';

/**
 * Zwei kuratierte Conversation-Style-Vorlagen, die der „Beratende
 * Tätigkeit"-Toggle ein- und ausblendet. Beide enthalten den
 * verpflichtenden `=== Konversations-Stil ===`-Header, damit der
 * voice-agent sie als Override des Default-Blocks erkennt.
 *
 * Der Voice-Agent liest am Ende nur den Text aus
 * `promptConversationStyle` — dieser Toggle setzt ihn lediglich.
 */
const CONVERSATION_STYLE_CONSULTATIVE =
  '=== Konversations-Stil ===\n' +
  'Du sprichst freundlich, aufmerksam und beratend. Höre zu, bevor du antwortest. ' +
  'Wenn der Anrufer ein Anliegen schildert, fasse es kurz in eigenen Worten zusammen, ' +
  'bevor du antwortest — so spürt der Anrufer, dass er gehört wurde.\n\n' +
  'Stelle gezielte Rückfragen statt eine Liste abzuhaken. Bei Unsicherheit gib eine ' +
  'fachlich fundierte Empfehlung ab und mache transparent, dass die endgültige ' +
  'Entscheidung bei der Person vor Ort liegt. Pro Redezug EINE Frage, klare Sätze, ' +
  'keine Floskeln.\n\n' +
  'Du DARFST aktiv beraten und Handlungsempfehlungen aussprechen, sofern sie auf der ' +
  'Wissensbasis beruhen. Bei Themen, die nicht in der Wissensbasis stehen: nenne ein ' +
  'Rückrufangebot statt zu spekulieren.';

const CONVERSATION_STYLE_FACTUAL =
  '=== Konversations-Stil ===\n' +
  'Du sprichst freundlich, sachlich und effizient. Halte dich kurz und stelle nur EINE ' +
  'Frage pro Redezug.\n\n' +
  'Du nimmst die Eckdaten auf und gibst sie sauber weiter — du beratest nicht und gibst ' +
  'keine eigenen Empfehlungen ab. Der Mensch entscheidet, du protokollierst.\n\n' +
  'Wenn der Anrufer eine Frage stellt: beantworte sie nur, wenn die Antwort eindeutig ' +
  'aus der Wissensbasis hervorgeht. Andernfalls kündige Rückruf an. Pro Redezug eine ' +
  'Frage, klare Sätze, keine Floskeln.';

/** Map der vier vollständig gesperrten Verhaltens-Override-Blöcke.
 *  Der Konversations-Stil ist editierbar (über den Beratungs-Toggle),
 *  alle anderen sind read-only weil sie systemisch verankert sind und
 *  ungewollt das Tool-Calling oder DSGVO-Verhalten brechen können. */
const FULLY_LOCKED_FIELDS = ADMIN_LOCKED_PROMPT_FIELDS.filter(
  (f) => f.key !== 'promptConversationStyle',
);

interface Props {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}

export function LlmTab({ form, setForm }: Props) {
  const { toast } = useToast();
  const adminPayload = useTargetUidPayload();
  const [introBusy, setIntroBusy] = React.useState(false);
  const [outroBusy, setOutroBusy] = React.useState(false);

  const providerEntry = LLM_CATALOG[form.llmProvider];
  const modelDetails = findModel(form.llmProvider, form.llmModel);

  async function regenerateIntro() {
    setIntroBusy(true);
    try {
      const fn = httpsCallable<
        { phrase?: string; voiceId?: string; model?: string; uid?: string },
        { ok: true; url: string }
      >(functions, 'generateIntroAudio');
      const r = await fn({
        phrase: form.introPhrase,
        voiceId: form.ttsVoiceId,
        model: form.ttsModel,
        ...adminPayload,
      });
      setForm((p) => ({ ...p, introAudioUrl: r.data.url }));
      toast({
        title: 'Intro generiert',
        description: 'Wird beim nächsten Anruf abgespielt.',
      });
    } catch (err) {
      toast({
        title: 'Intro-Generierung fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setIntroBusy(false);
    }
  }

  function previewIntro() {
    if (!form.introAudioUrl) return;
    const audio = new Audio(`${form.introAudioUrl}?t=${Date.now()}`);
    void audio.play().catch(() => undefined);
  }

  async function regenerateOutro() {
    setOutroBusy(true);
    try {
      const fn = httpsCallable<
        { phrase?: string; voiceId?: string; model?: string; uid?: string },
        { ok: true; url: string }
      >(functions, 'generateOutroAudio');
      const r = await fn({
        phrase: form.outroPhrase ?? undefined,
        voiceId: form.ttsVoiceId,
        model: form.ttsModel,
        ...adminPayload,
      });
      setForm((p) => ({ ...p, outroAudioUrl: r.data.url }));
      toast({
        title: 'Verabschiedung gerendert',
        description: 'Wird beim nächsten Auflegen abgespielt.',
      });
    } catch (err) {
      toast({
        title: 'Generierung fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setOutroBusy(false);
    }
  }

  function previewOutro() {
    if (!form.outroAudioUrl) return;
    const audio = new Audio(`${form.outroAudioUrl}?t=${Date.now()}`);
    void audio.play().catch(() => undefined);
  }

  return (
    <div className="space-y-6">
      <OnboardingHero />
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Provider & Modell</CardTitle>
            <CardDescription>Beeinflusst Latenz, Qualität und Kosten direkt.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Provider</Label>
                <Select
                  value={form.llmProvider}
                  onValueChange={(v) => {
                    const p = v as LlmProvider;
                    const first = LLM_CATALOG[p].models[0]?.id ?? '';
                    setForm((prev) => ({ ...prev, llmProvider: p, llmModel: first }));
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.values(LLM_CATALOG).map((entry) => (
                      <SelectItem key={entry.provider} value={entry.provider}>{entry.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Modell</Label>
                {providerEntry.freeForm ? (
                  <Input
                    value={form.llmModel}
                    placeholder="z.B. anthropic/claude-haiku-4.5"
                    onChange={(e) => setForm((p) => ({ ...p, llmModel: e.target.value }))}
                  />
                ) : (
                  <Select
                    value={form.llmModel}
                    onValueChange={(v) => setForm((p) => ({ ...p, llmModel: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {providerEntry.models.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            {modelDetails && (
              <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                {modelDetails.ttftP50Ms != null && <>TTFT p50 ≈ {modelDetails.ttftP50Ms} ms · </>}
                {modelDetails.inputUsdPerMTok != null && <>Input ${modelDetails.inputUsdPerMTok}/MTok · </>}
                {modelDetails.outputUsdPerMTok != null && <>Output ${modelDetails.outputUsdPerMTok}/MTok</>}
                {modelDetails.note && <div className="mt-1 text-foreground">{modelDetails.note}</div>}
              </div>
            )}
            {modelDetails && modelDetails.supportsTools === false && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
                <strong>Achtung:</strong> Dieses Modell unterstützt keine
                Tool-Calls. Termin-Buchungen, Ticket-Erstellung, Kalender-
                Abfragen und Rückrufe werden{' '}
                <strong>nicht funktionieren</strong> — der Anrufer hört dann nur
                Filler-Geräusche, weil das Modell mit leerem Content antwortet.
                Wähle für den Voice-Agent nur Modelle mit Tool-Use-Support.
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <SliderField
                label={`Temperature: ${form.llmTemperature.toFixed(2)}`}
                value={form.llmTemperature}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => setForm((p) => ({ ...p, llmTemperature: v }))}
              />
              <SliderField
                label={`Max Tokens: ${form.llmMaxTokens}`}
                hint="Klein halten — Voice-Antworten sollten kurz sein."
                value={form.llmMaxTokens}
                min={50}
                max={2000}
                step={10}
                onChange={(v) => setForm((p) => ({ ...p, llmMaxTokens: Math.round(v) }))}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Begrüßung beim Anrufstart</CardTitle>
            <CardDescription>
              Erste Phrase, die der Anrufer hört — vorab mit der unter „Stimme & Sprache"
              gewählten ElevenLabs-Stimme gerendert. Wird beim Speichern automatisch neu
              generiert, wenn sich Text oder Stimme geändert haben.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              rows={3}
              value={form.introPhrase ?? ''}
              placeholder="Hallo, mein Name ist Lisa. Wie kann ich Ihnen heute helfen?"
              onChange={(e) => setForm((p) => ({ ...p, introPhrase: e.target.value }))}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void regenerateIntro()}
                disabled={introBusy || !form.introPhrase?.trim()}
              >
                {introBusy ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                )}
                Neu generieren
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={previewIntro}
                disabled={!form.introAudioUrl}
              >
                <Play className="mr-1.5 h-3.5 w-3.5" />
                Vorhören
              </Button>
              <span className="text-xs text-muted-foreground">
                {form.introAudioUrl ? 'Audio gerendert' : 'Noch nicht generiert'}
                {' · '}
                {(form.introPhrase ?? '').length} / 500
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Verabschiedung beim Auflegen</CardTitle>
            <CardDescription>
              Letzter Satz, den der Anrufer hört, bevor der Agent auflegt.
              Wird einmal in der gewählten Stimme vorab gerendert und vom
              `end_call`-Tool direkt aus der Datei abgespielt — danach legt
              die Pipeline selbstständig auf. Beim Speichern automatisch
              neu generiert, wenn sich Text, Stimme oder TTS-Modell geändert
              haben.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              rows={3}
              value={form.outroPhrase ?? ''}
              placeholder="Vielen Dank für Ihren Anruf. Auf Wiederhören!"
              onChange={(e) =>
                setForm((p) => ({ ...p, outroPhrase: e.target.value }))
              }
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void regenerateOutro()}
                disabled={outroBusy || !form.outroPhrase?.trim()}
              >
                {outroBusy ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                )}
                Neu generieren
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={previewOutro}
                disabled={!form.outroAudioUrl}
              >
                <Play className="mr-1.5 h-3.5 w-3.5" />
                Vorhören
              </Button>
              <span className="text-xs text-muted-foreground">
                {form.outroAudioUrl ? 'Audio gerendert' : 'Noch nicht generiert'}
                {' · '}
                {(form.outroPhrase ?? '').length} / 500
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System-Prompt</CardTitle>
            <CardDescription>
              Charakter, Tonalität, Vorgehen. Wird beim Anruf automatisch um Termin-Typen
              und die aktuelle Uhrzeit ergänzt.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Textarea
              rows={10}
              value={form.systemPrompt}
              onChange={(e) => setForm((p) => ({ ...p, systemPrompt: e.target.value }))}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{form.systemPrompt.length} / 8000 Zeichen</span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setForm((p) => ({ ...p, systemPrompt: p.systemPrompt + '\n\nVerfügbare Termin-Typen: {EVENT_TYPES_LISTING}' }))}
              >
                <Sparkles className="mr-1 h-3.5 w-3.5" /> EventType-Platzhalter einfügen
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Wissensbasis</CardTitle>
            <CardDescription>
              Verbindliche Fakten, auf die der Agent zugreifen soll: Öffnungszeiten,
              Preise, Adressen, FAQ. Wird vor dem Gespräch automatisch in den Prompt
              eingefügt — nichts hier wird vom LLM erfunden, alles wird zitiert.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Textarea
              rows={12}
              value={form.knowledgeBase ?? ''}
              placeholder={
                'Praxis-Adresse: Beispielstraße 1, 1010 Wien\n' +
                'Öffnungszeiten: Mo–Fr 09:00–18:00\n' +
                'Notfallnummer: +43 1 23456789\n' +
                'Preise: Erstberatung 60 €, Folgetermin 40 €\n' +
                '\n' +
                'FAQ:\n' +
                'Q: Bieten Sie Hausbesuche an?\n' +
                'A: Ja, Mittwoch nachmittags nach Vereinbarung.'
              }
              onChange={(e) => setForm((p) => ({ ...p, knowledgeBase: e.target.value }))}
            />
            <div className="text-xs text-muted-foreground">
              {(form.knowledgeBase ?? '').length} / 20.000 Zeichen
            </div>
          </CardContent>
        </Card>

        <ConversationStyleCard form={form} setForm={setForm} />
        <LockedRulesCard />
      </div>
    </div>
  );
}

function SliderField({
  label, value, min, max, step, hint, onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  hint?: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={(vs) => onChange(vs[0] ?? value)} />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/**
 * Hero-Card am Kopf des KI-Modell-Tabs. Öffnet den Fullscreen-
 * Onboarding-Wizard, der die User-Antworten via Gemini komprimiert
 * und direkt in System-Prompt, Wissensbasis, Begrüßung und
 * Konversations-Stil schreibt.
 */
function OnboardingHero() {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Card className="border-primary/40 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            Geführtes Onboarding
          </CardTitle>
          <CardDescription>
            Lass dich Schritt für Schritt durch die Einrichtung führen — Öffnungszeiten,
            Angebot, FAQ, optionaler Dokumenten-Upload. Gemini komprimiert deine
            Antworten anschließend automatisch in System-Prompt, Wissensbasis und
            Konversations-Stil. Zwischendurch kannst du jederzeit überspringen oder
            später nachschärfen — alle Eingaben werden automatisch gesichert.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button type="button" size="lg" onClick={() => setOpen(true)}>
            <Sparkle className="mr-2 h-4 w-4" /> Onboarding starten
          </Button>
        </CardContent>
      </Card>
      <OnboardingWizard open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/**
 * Editierbarer Konversations-Stil mit Beratungs-Toggle. Wenn der Toggle
 * gesetzt wird, schreibt die UI eine kuratierte Beratungs- bzw.
 * Sachlich-Vorlage in `promptConversationStyle`. Der User kann den
 * Text danach jederzeit nachjustieren — der Toggle merkt sich nur
 * seinen letzten Zustand und überschreibt das Feld erst beim nächsten
 * Wechsel wieder.
 */
function ConversationStyleCard({ form, setForm }: Props) {
  const consultative = form.consultativeMode ?? false;
  const stored = form.promptConversationStyle ?? '';

  function applyToggle(next: boolean) {
    const template = next ? CONVERSATION_STYLE_CONSULTATIVE : CONVERSATION_STYLE_FACTUAL;
    setForm((p) => ({
      ...p,
      consultativeMode: next,
      promptConversationStyle: template,
    }));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Konversations-Stil</CardTitle>
        <CardDescription>
          Steuert Tonalität und Disziplin der Gesprächsführung. Wähle, ob die KI
          aktiv beraten oder nur sachlich aufnehmen soll — der Toggle füllt das
          Textfeld automatisch mit der passenden Vorlage. Du kannst den Text
          danach feintunen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-md border border-border bg-muted/20 p-3">
          <div className="space-y-0.5">
            <Label htmlFor="consultative-toggle" className="text-sm font-medium">
              Beratende Tätigkeit
            </Label>
            <p className="text-xs text-muted-foreground">
              {consultative
                ? 'KI berät aktiv und gibt Empfehlungen aus der Wissensbasis.'
                : 'KI nimmt sachlich auf, ohne unaufgefordert zu beraten.'}
            </p>
          </div>
          <Switch
            id="consultative-toggle"
            checked={consultative}
            onCheckedChange={applyToggle}
          />
        </div>
        <Textarea
          rows={10}
          value={stored}
          placeholder={consultative ? CONVERSATION_STYLE_CONSULTATIVE : CONVERSATION_STYLE_FACTUAL}
          className="font-mono text-xs"
          onChange={(e) =>
            setForm((p) => ({ ...p, promptConversationStyle: e.target.value || null }))
          }
        />
        <div className="text-[11px] text-muted-foreground">
          {stored.length.toLocaleString('de-DE')} Zeichen · {consultative ? 'Beratungs-Modus' : 'Sachlich-Modus'}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Read-only-Anzeige der vier systemisch verankerten Verhaltensregeln
 * (Anrufer-Identität, Buchungs-Workflow, Ticket-Erstellung,
 * Ticket-Auskunft). Diese Blöcke MÜSSEN unverändert bleiben, weil sie
 * mit Tool-Calling, DSGVO-Logik und Workflow-Reihenfolge im Backend
 * verzahnt sind. Falsch geänderte Texte können stille Datenleaks oder
 * abgeschnittene Tool-Calls verursachen — daher kein Edit-Pfad.
 */
function LockedRulesCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-muted-foreground" />
          Gesperrte Verhaltensregeln
        </CardTitle>
        <CardDescription>
          Vier system-verankerte Regel-Blöcke, die der voice-agent automatisch an
          den System-Prompt anhängt. Diese sind aus gutem Grund nicht editierbar —
          sie steuern Tool-Calling-Reihenfolge, DSGVO-konformes Verhalten und
          Workflow-Disziplin. Wenn du sie wirklich anpassen musst, sprich uns
          direkt an.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {FULLY_LOCKED_FIELDS.map((field) => (
          <div key={field.key} className="rounded-md border border-border bg-muted/10 p-3">
            <div className="mb-1.5 flex items-center gap-2">
              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm font-medium">{field.label}</span>
            </div>
            <p className="mb-2 text-[11px] text-muted-foreground">{field.helper}</p>
            <pre className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded bg-card p-2 font-mono text-[11px] leading-relaxed text-muted-foreground">
              {field.default}
            </pre>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
